import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useDisclosure } from '@chakra-ui/react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Link,
  Text,
  VStack
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import StorageManager from '../../utils/StorageManager';
import LegalModal from './LegalModal';
import axios from 'axios';

/**
 * TermsGate — Global wrapper that ensures every visitor has accepted
 * the Terms & Conditions before viewing any content.
 *
 * FLOW (Polish Run 1 §4 redesign, owner-approved):
 *  - While NOT accepted, the app (children) is mounted UNDERNEATH an opaque
 *    full-screen gate — Home + data + thumbnails load behind it.
 *  - Clicking Accept/Sign In/Join:
 *      1. INSTANT feedback — the terms card animates away (fade + slide up) so
 *         the click never hits a "concrete wall".
 *      2. Proof is recorded in the background.
 *      3. The remaining dark overlay fades out the MOMENT the background load
 *         finishes (min ~1s after the click so the eye settles, hard cap ~3.5s
 *         if the load is slow) — no fixed dead hang.
 *  - The overlay is above the index splash (z 10001 vs 9999) so the gate shows
 *    first while the page loads behind it.
 *
 * Exemptions: /terms /privacy /signup, and authenticated users.
 */
const CARD_OUT_MS = 420;   // terms card fade+slide duration
const OVERLAY_FADE_MS = 650; // final dark-overlay fade duration
const MIN_HOLD_MS = 1000;  // shortest time the dark overlay stays after the card leaves
const MAX_HOLD_MS = 3500;  // hard cap — never trap the user behind a broken load

// Self-contained keyframes (no CSS file changes needed)
const keyframes = `
@keyframes tgCardOut {
  0%   { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(-46px) scale(0.94); }
}
@keyframes tgBtnPulse {
  0%   { transform: scale(1); }
  40%  { transform: scale(0.94); }
  100% { transform: scale(1); }
}`;

const TermsGate = ({ children }) => {
  const legalModal = useDisclosure(); // Terms & Privacy chooser
  const [stage, setStage] = useState(() => {
    // 'gated' -> 'clicked' (card animating out) -> 'leaving' (overlay fading) -> 'gone'
    try {
      const stored = localStorage.getItem('burrow_terms_accepted');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.accepted === true) return 'gone';
      }
    } catch (e) { /* corrupted — treat as not accepted */ }
    return 'gated';
  });
  const location = useLocation();
  const navigate = useNavigate();

  const isAuthenticated = Boolean(StorageManager.get('burrowtoken'));
  const exemptPaths = ['/terms', '/privacy', '/signup', '/status'];
  const isExempt = exemptPaths.some(p => location.pathname.startsWith(p));

  // Background-load state — the overlay fades out when this settles
  const dataLoading = useSelector(state => state.Mode?.loading);
  const movieCount = useSelector(state => state.Mode?.dataSource?.movies?.length || 0);
  const dataReadyRef = useRef(false);
  dataReadyRef.current = !dataLoading && movieCount > 0;
  const pollRef = useRef(null);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  if (isAuthenticated || isExempt) {
    return children;
  }

  const gated = stage !== 'gone';

  const getSessionFingerprint = () => {
    let fp = localStorage.getItem('burrow_session_fingerprint');
    if (!fp) {
      fp = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
      localStorage.setItem('burrow_session_fingerprint', fp);
    }
    return fp;
  };

  // Click → instant card exit, then fade the overlay once the load settles.
  const dismissGate = (afterHold) => {
    if (stage !== 'gated') return;
    setStage('clicked');

    const clickTime = Date.now();
    const finishFade = () => setStage('leaving');
    const finishGone = () => {
      setStage('gone');
      if (afterHold) afterHold();
    };

    // When the behind-load is ready (or the cap expires), fade the dark overlay.
    const check = () => {
      const elapsed = Date.now() - clickTime;
      if (dataReadyRef.current && elapsed >= MIN_HOLD_MS) {
        clearInterval(pollRef.current);
        finishFade();
        setTimeout(finishGone, OVERLAY_FADE_MS);
      } else if (elapsed >= MAX_HOLD_MS) {
        clearInterval(pollRef.current);
        finishFade();
        setTimeout(finishGone, OVERLAY_FADE_MS);
      }
    };
    pollRef.current = setInterval(check, 120);
    check(); // try immediately — data may already be loaded behind the gate
  };

  const handleAccept = () => {
    recordAcceptance();
    dismissGate();
  };

  const handleSignIn = () => {
    recordAcceptance();
    dismissGate(() => navigate('/home', { state: { openSignIn: true } }));
  };

  const handleJoin = () => {
    recordAcceptance();
    dismissGate(() => navigate('/signup'));
  };

  const recordAcceptance = async () => {
    try {
      const sessionFingerprint = getSessionFingerprint();
      localStorage.setItem('burrow_terms_accepted', JSON.stringify({
        accepted: true,
        version: '1.0',
        timestamp: new Date().toISOString(),
        fingerprint: sessionFingerprint
      }));
      const baseUrl = process.env.REACT_APP_API_URL || '';
      await axios.post(`${baseUrl}/api/legal/accept-terms`, {
        termsVersion: '1.0',
        acceptanceType: 'gateway_enter',
        sessionFingerprint
      });
    } catch (err) {
      console.error('[TermsGate] Failed to record acceptance:', err.message);
    }
  };

  const cardAnimation = stage === 'clicked' || stage === 'leaving'
    ? 'tgCardOut ' + CARD_OUT_MS + 'ms cubic-bezier(0.4, 0, 0.2, 1) forwards'
    : undefined;

  return (
    <>
      {/* The app is ALWAYS mounted underneath — it loads while the gate is up */}
      {children}

      {gated && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="black"
          zIndex={10001}
          display="flex"
          alignItems="center"
          justifyContent="center"
          opacity={stage === 'leaving' || stage === 'gone' ? 0 : 1}
          transition={'opacity ' + OVERLAY_FADE_MS + 'ms ease'}
          pointerEvents={stage === 'clicked' || stage === 'leaving' ? 'none' : 'auto'}
        >
          <style>{keyframes}</style>

          {/* Background image layer — BurrowBackDark1 at 50% over solid black (owner 9-4-26) */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgImage="url('https://BurrowPublic.b-cdn.net/images%20burrow-lite/BurrowBackDark1.png')"
            bgPosition="center"
            bgSize="cover"
            bgRepeat="no-repeat"
            opacity={0.25}
            pointerEvents="none"
          />

          <Box
            position="relative"
            bg="blackAlpha.800"
            backdropFilter="blur(12px)"
            borderRadius="xl"
            border="1px"
            borderColor="whiteAlpha.200"
            p={{ base: 6, md: 10 }}
            maxW="500px"
            w="90%"
            textAlign="center"
            boxShadow="2xl"
            animation={cardAnimation}
          >
            <VStack spacing={6}>
              <Image
                src="https://bunny-burrow-pull-1.b-cdn.net/ROKU-PNG-Logo-Longlow.png"
                alt="Burrow"
                h={{ base: "16", md: "20" }}
              />

              <Heading size="lg" color="white">
                Welcome to Burrow
              </Heading>

              <Text color="gray.300" fontSize="md">
                This is a private assembly platform. Before continuing, you must agree to our membership terms.
              </Text>

              <Text color="gray.400" fontSize="sm" lineHeight="tall">
                By clicking <strong style={{ color: '#D69E2E' }}>Accept &amp; Continue</strong>, <strong style={{ color: '#D69E2E' }}>Sign In</strong>, or <strong style={{ color: '#D69E2E' }}>Join</strong>, you agree to our{" "}
                <Link
                  as="a"
                  href="#"
                  onClick={(e) => { e.preventDefault(); legalModal.onOpen(); }}
                  color="yellow.400"
                  textDecoration="underline"
                  _hover={{ color: "yellow.300" }}
                >
                  Terms &amp; Conditions
                </Link>
                {" "}and become a community member of our private assembly, subject to its rules and agreements.
              </Text>

              <Button
                bg="yellow.600"
                color="white"
                size="lg"
                w="full"
                _hover={{ bg: "yellow.500", transform: "scale(1.02)" }}
                transition="all 0.2s"
                _active={{ transform: "scale(0.96)" }}
                animation={stage === 'gated' ? undefined : 'tgBtnPulse 0.25s ease'}
                onClick={handleAccept}
              >
                Accept &amp; Continue
              </Button>

              <Flex w="full" gap={3}>
                <Button
                  variant="outline"
                  color="white"
                  borderColor="whiteAlpha.400"
                  size="md"
                  flex="1"
                  _hover={{ bg: "whiteAlpha.100", borderColor: "yellow.400" }}
                  transition="all 0.2s"
                  onClick={handleSignIn}
                >
                  Sign In
                </Button>
                <Button
                  variant="outline"
                  color="yellow.400"
                  borderColor="yellow.600"
                  size="md"
                  flex="1"
                  _hover={{ bg: "yellow.600", color: "white" }}
                  transition="all 0.2s"
                  onClick={handleJoin}
                >
                  Join
                </Button>
              </Flex>
            </VStack>
          </Box>
        </Box>
      )}

      {/* Legal documents chooser — above the gate overlay */}
      <LegalModal isOpen={legalModal.isOpen} onClose={legalModal.onClose} />
    </>
  );
};

export default TermsGate;
